// Public API: `analytics.track(area, feature, sub_feature?, props?)`.
//
// Guarantees:
//   - Never throws.
//   - Never blocks.
//   - Silently drops the event when:
//       * user is not authenticated
//       * `analytics.tracked_roles` for the active client is a non-empty
//         allow-list and the user's role slugs don't intersect it
//         (default `[]` = collect for every authenticated role)
//       * a bug / storage failure occurs (best-effort)
//
// The BE re-applies the same role check server-side. This client filter is
// a bandwidth optimisation, not a security control.

import { useAuthStore } from '@/shared/stores/auth.store'
import { ANALYTICS_CONFIG, UUID_RE } from './config'
import type { AnalyticsArea, AnalyticsPrimitive, AnalyticsProps } from './config'
import { normalisePath } from './path-normalise'
import { enqueue } from './queue'

// Suppress identical `(area, feature, sub_feature, path)` events fired within
// this window. Guards against rage-clicking a tab, StrictMode double-invoke,
// and any handler that ends up wired twice by mistake. Real user intent to
// re-visit the same tab after half a second still emits.
const DEDUP_WINDOW_MS = 500
const lastEmit = new Map<string, number>()

const TRACKED_ROLES_KEY = 'analytics.tracked_roles'
// Sentinel a client can set as tracked_roles to opt out entirely — mirrors
// the BE service's constant. See src/services/analytics/analytics.service.ts.
const DISABLE_SENTINEL = '__none__'

/**
 * Read the client's `analytics.tracked_roles` allow-list from the auth
 * store's cached feature vector.
 *   - Returns an array on success (possibly empty).
 *   - Returns `null` when the value is missing, malformed, or contains the
 *     disable sentinel. Callers MUST fail-closed on null. Matches the BE.
 */
function readTrackedRoles(): readonly string[] | null {
  try {
    const user = useAuthStore.getState().user
    if (!user) return null
    const clientId = user.active_client?.id
    if (!clientId) return null
    const vector = user.features?.vectors?.[clientId]
    const defaults = user.features?.defaults ?? {}
    const raw =
      vector && Object.prototype.hasOwnProperty.call(vector, TRACKED_ROLES_KEY)
        ? vector[TRACKED_ROLES_KEY]
        : defaults[TRACKED_ROLES_KEY]
    if (Array.isArray(raw) && raw.every((v) => typeof v === 'string')) {
      const arr = raw as string[]
      if (arr.includes(DISABLE_SENTINEL)) return null
      return arr
    }
  } catch {
    /* fall through */
  }
  return null
}

function currentPathname(): string | undefined {
  try {
    // Include normalised query for the routes that carry a meaningful
    // ?tab= param (e.g. /search?tab=icd). normalisePath keeps the tab and
    // strips everything else, so we don't leak arbitrary query strings.
    return normalisePath(window.location.pathname, window.location.search)
  } catch {
    return undefined
  }
}

function stripUuidsInProps(props: AnalyticsProps): AnalyticsProps {
  const out: AnalyticsProps = {}
  for (const [k, v] of Object.entries(props)) {
    out[k] = typeof v === 'string' ? v.replace(UUID_RE, ':id') : v
  }
  return out
}

function isAllowed(): boolean {
  const user = useAuthStore.getState().user
  if (!user) return false
  const tracked = readTrackedRoles()
  // Fail-closed: null (missing, malformed, or disable sentinel) → drop.
  if (tracked === null) return false
  // Empty list = collect for every authenticated role. Non-empty = allow-list.
  if (tracked.length === 0) return user.roles.length > 0
  return user.roles.some((r) => tracked.includes(r.name))
}

function primitiveOnly(v: unknown): v is AnalyticsPrimitive {
  const t = typeof v
  return t === 'string' || t === 'number' || t === 'boolean'
}

function coerceProps(input?: Record<string, unknown>): AnalyticsProps | undefined {
  if (!input) return undefined
  const out: AnalyticsProps = {}
  for (const [k, v] of Object.entries(input)) {
    if (primitiveOnly(v)) out[k] = v
  }
  if (Object.keys(out).length === 0) return undefined
  if (JSON.stringify(out).length > ANALYTICS_CONFIG.MAX_PROPS_BYTES) return undefined
  return stripUuidsInProps(out)
}

/**
 * Record one product-usage event. Fire-and-forget. Called from route-view
 * hooks (`use-page-view.ts`) and from a small number of state-driven tab
 * handlers documented in `docs/analytics/README.md`.
 */
export function track(
  area: AnalyticsArea,
  feature: string,
  subFeature?: string,
  props?: Record<string, unknown>,
): void {
  try {
    if (!isAllowed()) return
    const path = currentPathname()
    const key = `${path ?? ''}|${area}|${feature}|${subFeature ?? ''}`
    const now = Date.now()
    const prev = lastEmit.get(key)
    if (prev !== undefined && now - prev < DEDUP_WINDOW_MS) return
    lastEmit.set(key, now)
    // Bounded map — evict stale entries lazily to prevent growth.
    if (lastEmit.size > 200) {
      for (const [k, ts] of lastEmit) {
        if (now - ts > 60_000) lastEmit.delete(k)
      }
    }
    enqueue({
      event_time: new Date().toISOString(),
      area,
      feature,
      sub_feature: subFeature || undefined,
      page_path: path,
      props: coerceProps(props),
    })
  } catch {
    /* never throw */
  }
}

/** Namespaced default export for the ergonomic `analytics.track(...)` call. */
export const analytics = { track }

export type { AnalyticsArea, AnalyticsProps }
